import { session } from './index';
import { isHydraRoute } from '@/services/router/route-ownership';
import { hardRedirect } from '@/utils/hard-redirect';

let registered = false;

function handleInvalidation() {
  // The tab that initiated logout owns its own redirect from the `/logout`
  // route; only peer / programmatic invalidations need handling here.
  if (window.location.pathname.startsWith('/logout')) {
    return;
  }

  // Redirect only — deliberately no localStorage-clear or session-prune here,
  // unlike Ember's `handleInvalidation`. Ember bundled that cleanup in because
  // its handler was the sole place all invalidation work ran; in Hydra the
  // acting tab's `LogoutPage` already does it (clears `cio-features` /
  // `cio-settings` and prunes the slot via `changeToSession(0)` +
  // `updateSessions`). Both propagate to this peer tab automatically —
  // localStorage is shared across same-origin tabs, and `updateSessions`
  // broadcasts a metadata update over the session BroadcastChannel — so
  // repeating them here would be redundant. The only thing that doesn't
  // propagate is moving *this* tab off its authenticated page, which is exactly
  // what the redirect (and its full reload, clearing in-memory caches) does.
  hardRedirect('/login');
}

/**
 * Redirect to `/login` when the active session is invalidated by something other
 * than this tab's own `/logout` navigation — a peer tab logging out (delivered
 * over the session's cross-tab BroadcastChannel) or any programmatic
 * `session.invalidate()`. The full-document `hardRedirect` also tears down this
 * tab's in-memory caches (TanStack Query, the cross-app cache), so no logged-out
 * account's data lingers.
 *
 * Restores the app-wide `session.on('invalidated')` → redirect that Ember's
 * long-lived session service had (`handleInvalidation`). Hydra moved that logic
 * into the `/logout` route component, so it only ran in the tab that actually
 * navigated there — peer tabs kept stale auth state and caches in memory until
 * the user next interacted.
 *
 * Registered once at app boot (`main.tsx`); idempotent. This is an app-lifetime
 * subscription — there is no production teardown (the listener lives as long as
 * the tab, and boot runs once per page load). Tests use
 * `resetSessionInvalidationRedirect` to detach it between cases.
 *
 * Gated on Hydra owning the auth routes. `Session` is a single shared instance
 * (`window.__hydra__.session`), and while the embedded Ember app still owns
 * `/login` its own session service already handles `invalidated` on that
 * instance (cleanup + redirect). Registering here too would double the redirect
 * and clobber Ember's `?loggedOut=1` / `?origin` params. Route ownership is the
 * source of truth that the cutover (which hands `/login` back to Hydra) flips, so
 * this stays a no-op until then and activates automatically with it.
 */
export function registerSessionInvalidationRedirect(): void {
  if (registered || !isHydraRoute('/login')) {
    return;
  }
  registered = true;
  session.on('invalidated', handleInvalidation);
}

/** Detach the listener and reset module state. Test-only — not for production use. */
export function resetSessionInvalidationRedirect(): void {
  if (!registered) {
    return;
  }
  session.off('invalidated', handleInvalidation);
  registered = false;
}
